/**
 * Live (AJAX) validation of entered meter readings.
 *
 * Both readings pages ask the server for the verdict instead of repeating the
 * min/max rules in the browser: those rules depend on supplier settings, the
 * standard yearly usage and the previous reading, none of which the browser
 * should have to know. The submit keeps its own validation, so this only ever
 * gives an earlier hint, never the final word.
 */

// Counted from the last keystroke, on both pages. Nothing at all happens while
// the next digit follows within this time: the spinner is tied to the call
// rather than to a timer of its own, so typing on can never make one flash.
// Long enough to type a whole meter reading without being judged halfway.
const DEBOUNCE_MS = 500;

// how long the submit button stays held after the last answer came in
const SUBMIT_ENABLE_DELAY_MS = 500;

/**
 * @returns {string} the CSRF token, or empty when the meta tag is gone
 */
function csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');

    return meta ? meta.content : '';
}

/**
 * @param {string} url - the validation endpoint
 * @param {Object} payload - flow id, date and the readings to judge
 * @returns {Promise<Object|null>} the verdict, or null when the call failed
 */
function postValidation(url, payload) {
    return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': csrfToken(),
        },
        body: JSON.stringify(payload),
    }).then(function (response) {
        return response.json().catch(function () {
            return null;
        });
    }).catch(function () {
        // A failed validation call must never block typing or submitting.
        return null;
    });
}

/**
 * Holds a submit button while answers are on their way.
 *
 * Submitting mid-flight would send a reading the page has not judged yet, and
 * the verdict would arrive after the fact. The button comes back a moment after
 * the last answer, so it does not flicker between two keystrokes.
 *
 * @param {HTMLElement|null} submit - the button to hold
 * @returns {Function} call with 1 when a call goes out and -1 when it returns
 */
function createSubmitGuard(submit) {
    let running = 0;
    let timer = null;

    return function (change) {
        running += change;

        if (!submit) {
            return;
        }

        window.clearTimeout(timer);

        if (running > 0) {
            submit.disabled = true;

            return;
        }

        timer = window.setTimeout(function () {
            submit.disabled = false;
        }, SUBMIT_ENABLE_DELAY_MS);
    };
}

/**
 * Keeps track of the field whose answer is pending. It is switched on when the
 * call goes out and off when the answer is in, so it exists exactly as long as
 * the customer is actually waiting for something.
 *
 * @returns {{start: Function, stop: Function}}
 */
function createSpinner() {
    let showing = [];

    return {
        start: function (inputs) {
            showing = inputs;
            setValidating(inputs, true);
        },
        stop: function () {
            setValidating(showing, false);
            showing = [];
        },
    };
}

/**
 * Show that an answer is on its way, at the left inside the field.
 *
 * Nothing is added to the page for it. The readings page puts its fields in an
 * input group together with the dial number and the decimals, and anything
 * inserted there pushes those onto the next line; the stylesheet draws the
 * spinner on the field itself, the same way the invalid mark is drawn.
 *
 * @param {HTMLElement[]} inputs - the fields waiting for a verdict
 * @param {boolean} validating - whether a call is on its way
 */
function setValidating(inputs, validating) {
    inputs.forEach(function (input) {
        input.classList.toggle('is-validating', validating);
    });
}

/**
 * A detail cell spans the whole table, which is wider than the screen. Its
 * content sticks to the left, and for that it needs a width of its own: the
 * visible width of the scrolling area.
 *
 * @param {HTMLElement} table - the readings table
 */
function syncDetailWidths(table) {
    const wrap = table.closest('.multiple-readings-table-wrap');

    if (!wrap) {
        return;
    }

    // exactly the visible width: any wider and the message runs out of room to
    // slide before the scrolling is done, and travels along over the last part
    table.querySelectorAll('.multiple-readings-detail-content').forEach(function (content) {
        content.style.width = wrap.clientWidth + 'px';
    });
}

/**
 * Slide a detail row open and closed instead of letting it jump into place.
 * The height is animated on the inner block, because a table row cannot be
 * transitioned reliably itself.
 *
 * @param {HTMLElement} detail - the detail row
 */
function openDetail(detail) {
    const content = detail.querySelector('.multiple-readings-detail-content');

    window.requestAnimationFrame(function () {
        content.classList.add('is-open');
    });
}

/**
 * @param {HTMLElement} detail - the detail row to slide shut and remove
 */
function closeDetail(detail) {
    const content = detail.querySelector('.multiple-readings-detail-content');

    detail.dataset.closing = '1';
    content.classList.remove('is-open');

    window.setTimeout(function () {
        // it may have failed again in the meantime, which clears the flag
        if (detail.dataset.closing === '1') {
            detail.remove();
        }
    }, 250);
}

/**
 * @param {Function} callback
 * @param {number} delay - milliseconds of quiet before it runs
 * @returns {Function}
 */
function debounce(callback, delay) {
    let timer = null;

    return function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(callback, delay);
    };
}

/**
 * The table page: one call per row, results shown in a detail row underneath.
 */
function initMultipleReadings() {
    const table = document.querySelector('.multiple-readings-table[data-validate-url]');

    if (!table) {
        return;
    }

    const url = table.dataset.validateUrl;
    const setRunning = createSubmitGuard(document.querySelector('.multiple-readings-actions button[type="submit"]'));

    /**
     * @param {HTMLInputElement} input
     * @returns {string} the dial this field carries, from its name attribute
     */
    function dialName(input) {
        return input.name.replace(/^.*\[readings]\[(.+)]$/, '$1');
    }

    /**
     * Apply a verdict to the row.
     *
     * The whole row is validated, because the allowed range of one dial depends
     * on the rest of it, but only the field that was edited is updated. Judging
     * the other fields too would put a verdict on values the customer has not
     * touched yet, and take away one they had just read.
     *
     * @param {HTMLElement} row - the row that was judged
     * @param {Object} result - what the server answered
     * @param {HTMLElement|null} source - the field that was edited, if any
     */
    function renderRow(row, result, source) {
        const flowId = row.dataset.flowId;
        const dials = (result && result.dials) || {};
        const sourceName = ((source && source.classList.contains('reading-value')) ? dialName(source) : null);

        if (! row.readingMessages) {
            row.readingMessages = { dials: {}, row: [] };
        }

        row.querySelectorAll('.reading-value').forEach(function (input) {
            const name = dialName(input);

            if (sourceName && (name !== sourceName)) {
                return;
            }

            const dial = dials[name];
            const invalid = Boolean(dial && dial.valid === false);

            input.classList.toggle('is-invalid', invalid);
            row.readingMessages.dials[name] = ((invalid && dial.message)
                ? (input.getAttribute('aria-label') || name) + ': ' + dial.message
                : null);
        });

        // these belong to the row as a whole, so they are left alone while a
        // single field is being corrected
        if (! sourceName) {
            row.readingMessages.row = ((result && result.errors) ? result.errors : []);
        }

        const messages = [];
        row.querySelectorAll('.reading-value').forEach(function (input) {
            const message = row.readingMessages.dials[dialName(input)];

            if (message) {
                messages.push(message);
            }
        });
        row.readingMessages.row.forEach(function (error) {
            messages.push(error);
        });

        let detail = table.querySelector('[data-flow-live="' + flowId + '"]');

        if (!messages.length) {
            if (detail) {
                closeDetail(detail);
            }

            return;
        }

        if (!detail) {
            detail = document.createElement('tr');
            detail.className = 'multiple-readings-detail multiple-readings-live-detail';
            detail.dataset.flowLive = flowId;
            // the table tells us how many columns it ended up with
            const columns = (table.dataset.columnCount || '10');
            detail.innerHTML = '<td colspan="' + columns + '"><div class="multiple-readings-detail-content"><ul></ul></div></td>';

            // directly under the fields it is about, above anything the last
            // submit left standing
            row.parentNode.insertBefore(detail, row.nextSibling);
        }

        // a row that was on its way out and fails again should simply stay
        delete detail.dataset.closing;

        detail.querySelector('ul').innerHTML = messages.map(function (message) {
            const item = document.createElement('li');
            item.textContent = message;

            return item.outerHTML;
        }).join('');

        syncDetailWidths(table);
        openDetail(detail);
    }

    /**
     * @param {HTMLElement} row - the row to judge
     * @param {{start: Function, stop: Function}} spinner
     * @param {HTMLElement|null} source - the field that was edited, if any
     */
    function validateRow(row, spinner, source) {
        const readings = {};
        // While typing, only the field being edited announces that its answer is on
        // its way. A row that was filled from a file has no single field to point
        // at, and then every filled one is waiting - so that is what it shows.
        const editing = Boolean(source && source.classList.contains('reading-value'));
        const pending = (editing ? [source] : []);
        let hasValue = false;

        row.querySelectorAll('.reading-value').forEach(function (input) {
            const name = input.name.replace(/^.*\[readings]\[(.+)]$/, '$1');
            readings[name] = input.value;

            if (input.value.trim() !== '') {
                hasValue = true;

                if (!editing) {
                    pending.push(input);
                }
            }
        });

        if (!hasValue) {
            spinner.stop();
            row.readingMessages = null;
            renderRow(row, null, null);

            return;
        }

        const dateInput = row.querySelector('.reading-date');

        spinner.start(pending);
        setRunning(1);

        postValidation(url, {
            flow_id: row.dataset.flowId,
            date: dateInput ? dateInput.value : '',
            readings: readings,
        }).then(function (result) {
            spinner.stop();
            setRunning(-1);

            if (result) {
                renderRow(row, result, source);
            }
        });
    }

    syncDetailWidths(table);
    window.addEventListener('resize', function () {
        syncDetailWidths(table);
    });

    table.querySelectorAll('.multiple-readings-row').forEach(function (row) {
        const spinner = createSpinner();
        let source = null;
        const validate = debounce(function () {
            validateRow(row, spinner, source);
        }, DEBOUNCE_MS);

        // the expected range for the date on the row, before anything is typed
        showExpectedRanges(row);

        // Typing is the only trigger: validating on blur as well produced a
        // second call, and a spinner, the moment the field was left.
        row.querySelectorAll('.reading-value').forEach(function (input) {
            input.addEventListener('input', function () {
                // it carried a value from a file until this moment
                input.classList.remove('is-imported');
                source = input;
                validate();
            });
        });

        // An import fills several fields of a row at once. Typing announces one
        // field at a time, and the verdict is applied to that one only; without
        // this the last field to be filled would be the only one judged, and the
        // rest of the row would sit there unchecked.
        row.addEventListener('readings:filled', function () {
            source = null;
            validate();
        });

        const dateInput = row.querySelector('.reading-date');

        if (dateInput) {
            dateInput.addEventListener('input', function () {
                showExpectedRanges(row);
                source = null;
                validate();
            });
            // the datepicker fills the field without a keystroke
            dateInput.addEventListener('change', function () {
                showExpectedRanges(row);
                validateRow(row, spinner, null);
            });
        }
    });
}

/**
 * Parse a d-m-Y date into a timestamp in seconds, or null when it is not one.
 *
 * @param {string} value
 * @returns {number|null}
 */
function parseDutchDate(value) {
    const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec((value || '').trim());

    if (!match) {
        return null;
    }

    const time = Date.UTC(Number(match[3]), Number(match[2]) - 1, Number(match[1]));

    return isNaN(time) ? null : time / 1000;
}

/**
 * The part of a year between the previous reading and the date on the row,
 * capped at one. The same rule as MultipleReadingsService::elapsedYearFraction(),
 * and it has to stay the same: the hint under the field and the verdict on
 * submit are meant to be one calculation.
 *
 * @param {string} previousDate - d-m-Y of the previous reading
 * @param {string} readingDate - d-m-Y the customer picked
 * @returns {number} between 0 and 1
 */
function elapsedYearFraction(previousDate, readingDate) {
    // the previous date comes from KAS as Y-m-d
    const previous = previousDate ? Date.parse(previousDate + 'T00:00:00Z') / 1000 : null;
    const reading = parseDutchDate(readingDate);

    if (previous === null || isNaN(previous) || reading === null || reading <= previous) {
        return 1;
    }

    const fraction = (reading - previous) / (365 * 86400);

    return (fraction <= 0 || fraction > 1) ? 1 : fraction;
}

/**
 * Write the expected range under every field of the row that has one.
 *
 * The parts come from the server as data attributes; only the date is applied
 * here, so what the customer sees moves with the date they pick and never
 * disagrees with what the submit will say.
 *
 * @param {HTMLElement} row - the row to write the hints into
 */
function showExpectedRanges(row) {
    const dateInput = row.querySelector('.reading-date');
    const readingDate = dateInput ? dateInput.value : '';

    row.querySelectorAll('.reading-value[data-range-syu]').forEach(function (input) {
        const hint = input.parentElement.querySelector('.multiple-readings-expected');

        if (!hint) {
            return;
        }

        const previous = Number(input.dataset.rangePrevious);
        const syu = Number(input.dataset.rangeSyu);
        const minimumOffset = Number(input.dataset.rangeMinimumOffset || 0);
        const fraction = elapsedYearFraction(input.dataset.rangePreviousDate, readingDate);

        // an empty factor is a bound that does not exist: no minimum for the low
        // dial of a meter that no longer switches, no maximum without a yearly usage
        const minimum = (input.dataset.rangeMinimumFactor === '')
            ? null
            : Math.trunc(previous + syu * Number(input.dataset.rangeMinimumFactor) * fraction + minimumOffset);
        const maximum = (input.dataset.rangeMaximumFactor === '')
            ? null
            : Math.trunc(previous + syu * Number(input.dataset.rangeMaximumFactor) * fraction);

        let range = '-';

        if (minimum !== null && maximum !== null) {
            range = minimum + ' - ' + maximum;
        } else if (maximum !== null) {
            range = '\u2264 ' + maximum;
        } else if (minimum !== null) {
            range = '\u2265 ' + minimum;
        }

        hint.textContent = hint.dataset.expectedLabel + ' ' + range;
    });
}

/**
 * The single readings page: same call, message shown under the field itself.
 */
function initSingleReadings() {
    const config = document.querySelector('.readings-validation-config[data-readings-validate-url]');

    if (!config) {
        return;
    }

    const url = config.dataset.readingsValidateUrl;
    const inputs = ['m1', 'm2', 'm3', 'm4', 'm5', 'g']
        .map(function (name) {
            return document.querySelector('[name="' + name + '"]');
        })
        .filter(function (input) {
            return input !== null;
        });

    if (!inputs.length) {
        return;
    }

    const spinner = createSpinner();
    // the save button of this page, held while an answer is on its way
    const setRunning = createSubmitGuard(document.getElementById('submit-button'));

    /**
     * @param {HTMLInputElement} input - the field the message belongs under
     * @param {string} message - what is wrong, empty to clear it
     */
    function showMessage(input, message) {
        let holder = input.readingMessageHolder || null;

        if (!message) {
            if (holder) {
                holder.remove();
                input.readingMessageHolder = null;
            }

            input.classList.remove('is-invalid');

            return;
        }

        if (!holder) {
            holder = document.createElement('div');
            holder.className = 'reading-live-message invalid-feedback d-block';

            // after the input group as a whole: put between the field and the
            // decimals it belongs to, it pushes those onto the next line
            const group = input.closest('.input-group') || input;
            group.parentNode.insertBefore(holder, group.nextSibling);
            input.readingMessageHolder = holder;
        }

        holder.textContent = message;
        input.classList.add('is-invalid');
    }

    /**
     * @param {HTMLElement|null} source - the field that was edited, if any
     */
    function validate(source) {
        const readings = {};
        let hasValue = false;

        inputs.forEach(function (input) {
            readings[input.name] = input.value;

            if (input.value.trim() !== '') {
                hasValue = true;
            }
        });

        if (!hasValue) {
            spinner.stop();
            inputs.forEach(function (input) {
                showMessage(input, '');
            });

            return;
        }

        const dateInput = document.querySelector('[name="date"]');

        // The page carries the request it was built for in a hidden field. Sending it
        // along keeps every call on that same request: without it the server looks an
        // open flow up again per keystroke, which costs a KAS call and can settle on
        // another type - other margins, and another answer about a photo.
        const flowInput = document.querySelector('[name="flow_id"]');

        spinner.start(source ? [source] : []);
        setRunning(1);

        postValidation(url, {
            flow_id: flowInput ? flowInput.value : 0,
            date: dateInput ? dateInput.value : '',
            readings: readings,
        }).then(function (result) {
            spinner.stop();
            setRunning(-1);

            if (!result) {
                return;
            }

            inputs.forEach(function (input) {
                // the field being edited is the only one whose verdict changes
                if (source && (input !== source)) {
                    return;
                }

                const dial = (result.dials || {})[input.name];

                showMessage(input, (dial && dial.valid === false) ? dial.message : '');
            });
        });
    }

    let source = null;
    const debounced = debounce(function () {
        validate(source);
    }, DEBOUNCE_MS);

    inputs.forEach(function (input) {
        // typing only; leaving the field would otherwise repeat the same call
        input.addEventListener('input', function () {
            source = input;
            debounced();
        });
    });
}

document.addEventListener('DOMContentLoaded', function () {
    initMultipleReadings();
    initSingleReadings();
});
